import React, { useEffect, useState } from 'react';
import { Bookmark, Trash2 } from 'lucide-react';
import { apiClient } from '../packages/api-client/index';

export interface SavedView {
  id: string;
  project_id: string;
  user_id: string;
  name: string;
  filters: Record<string, string>;
  created_at: string;
}

interface Props {
  projectId: string;
  currentFilters: Record<string, string>;
  onLoadView: (filters: Record<string, string>) => void;
}

export function SavedFilters({ projectId, currentFilters, onLoadView }: Props) {
  const [views, setViews] = useState<SavedView[]>([]);
  const [name, setName] = useState('');
  const [saving, setSaving] = useState(false);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    if (!open) return;
    apiClient.get<{ data: SavedView[] }>(`/projects/${projectId}/views`)
      .then(r => setViews(r.data.data ?? []))
      .catch(() => {});
  }, [projectId, open]);

  async function handleSave(e: React.FormEvent) {
    e.preventDefault();
    if (!name.trim()) return;
    setSaving(true);
    try {
      const { data } = await apiClient.post<SavedView>(`/projects/${projectId}/views`, {
        name: name.trim(),
        filters: currentFilters,
      });
      setViews(prev => [...prev, data]);
      setName('');
    } finally { setSaving(false); }
  }

  async function handleDelete(id: string) {
    await apiClient.delete(`/projects/${projectId}/views/${id}`);
    setViews(prev => prev.filter(v => v.id !== id));
  }

  return (
    <div style={{ position: 'relative', display: 'inline-block' }}>
      <button className="btn btn-ghost btn-sm" onClick={() => setOpen(o => !o)}>
        <Bookmark size={14} aria-hidden="true" style={{ verticalAlign: 'text-bottom' }} /> {views.length > 0 ? `Saved (${views.length})` : 'Save Filter'}
      </button>
      {open && (
        <div className="saved-filters-dropdown">
          <form onSubmit={handleSave} className="saved-filters-form">
            <input
              className="form-input"
              placeholder="View name…"
              value={name}
              onChange={e => setName(e.target.value)}
              style={{ flex: 1 }}
            />
            <button className="btn btn-primary btn-sm" type="submit" disabled={saving}>
              {saving ? '…' : 'Save'}
            </button>
          </form>
          {views.length === 0 ? (
            <p className="saved-filters-empty">No saved views yet</p>
          ) : (
            <ul className="saved-filters-list">
              {views.map(v => (
                <li key={v.id} className="saved-filter-row">
                  <button className="saved-filter-name" onClick={() => { onLoadView(v.filters); setOpen(false); }}>
                    {v.name}
                  </button>
                  <button className="btn btn-ghost btn-sm" style={{ color: 'var(--color-danger)', padding: '2px 5px' }}
                    aria-label="Delete saved view" onClick={() => handleDelete(v.id)}><Trash2 size={14} aria-hidden="true" /></button>
                </li>
              ))}
            </ul>
          )}
        </div>
      )}
    </div>
  );
}
